import {
  Button,
  Flex,
  Spinner,
  Text,
  useDisclosure,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { tagsApi } from '@texas/api/endpoints/tagsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '../shared/alert/ErrorDetails';
import { Icons } from '../shared/Icons';
import { EditTagLinksModal } from './EditTagLinksModal';
import { EntityType } from '@texas/api/endpoints/trackerApi';
import { TagWithTooltip } from '../shared/TagWithTooltip';
import { useSignalSubscriptionEvents } from '../shared/hooks/useSignalSubscriptionEvents';
import { tagEvents } from './events';

export interface EntityTagLinksProps {
  entityId: number;
  entityType: EntityType;
}

export function EntityTagLinks({ entityId, entityType }: EntityTagLinksProps) {
  const { t } = useTranslation();

  const {
    isOpen: isEditOpen,
    onClose: onEditClose,
    onOpen: onEditOpen,
  } = useDisclosure();

  const {
    data: tags,
    refetch,
    loading,
    error,
  } = useApiResource(tagsApi.getEntityTags);

  useEffect(() => {
    refetch(entityType, entityId);
  }, [refetch, entityType, entityId]);

  useSignalSubscriptionEvents({
    signalEvents: [tagEvents.tagUpdated],
    triggerOnEvent: () => {
      refetch(entityType, entityId);
    },
  });

  useEffect(
    () =>
      tagEvents.tagLinksUpdated.subscribe((event) => {
        if (event.entityId === entityId && event.entityType === entityType) {
          void refetch(entityType, entityId);
        }
      }),
    [entityId, entityType, refetch],
  );

  return (
    <Flex direction="column" gap={2}>
      <Flex gap={2}>
        <Text variant="header">{t('tags.title')}</Text>
        <Button
          size="sm"
          leftIcon={<Icons.Pencil />}
          variant="link"
          onClick={onEditOpen}
        >
          {t('tags.editTags')}
        </Button>
      </Flex>

      {loading && <Spinner size="sm" />}
      {error && <ErrorDetails error={error} />}

      {!loading && tags && (
        <Wrap spacing={2}>
          {tags.length === 0 ? (
            <Text color="gray.500" fontSize="sm">
              {t('tags.noTagsAssigned')}
            </Text>
          ) : (
            tags.map((tag) => (
              <WrapItem key={tag.id}>
                <TagWithTooltip key={tag.id} tag={tag} />
              </WrapItem>
            ))
          )}
        </Wrap>
      )}

      {tags && (
        <EditTagLinksModal
          isOpen={isEditOpen}
          onClose={onEditClose}
          entityId={entityId}
          entityType={entityType}
          currentTags={tags}
        />
      )}
    </Flex>
  );
}
